import { useTranslation } from 'react-i18next';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, ArrowRight, ExternalLink, Newspaper } from 'lucide-react';
import { useLocalizedNews } from '../data/useLocalizedNews';
import { getNewsBySlug } from '../data/news';


// News Card Component
const NewsCard = ({ nyhet, index }) => {
  const { t } = useTranslation();
  const isInternal = nyhet.link && nyhet.link.startsWith('/nyheter/');
  const hasExternalLink = nyhet.externalLink || nyhet.externalLinks;
  const useContainStyle = nyhet.imageStyle === 'contain' || nyhet.featured;

  const CardContent = () => (
    <>
      <div className={`aspect-video relative overflow-hidden ${useContainStyle ? 'bg-white flex items-center justify-center p-6' : ''}`}>
        <img
          src={nyhet.image}
          alt={nyhet.title}
          className={`${useContainStyle ? 'max-h-full max-w-full object-contain' : 'w-full h-full object-cover'} group-hover:scale-105 transition-transform duration-500`}
        />
        <div className="absolute top-3 left-3">
          <span className="bg-green-500 text-black text-xs font-bold px-2 py-1 rounded uppercase">
            {nyhet.category}
          </span>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-slate-400 text-sm mb-3">
          <Clock className="w-3 h-3" />
          {nyhet.date}
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-green-500 transition-colors line-clamp-2">
          {nyhet.title}
        </h3>
        <p className="text-slate-500 text-sm line-clamp-3">
          {nyhet.excerpt}
        </p>
        <span className="inline-flex items-center text-green-500 font-medium text-sm mt-4">
          {t('news.readMore')}
          {hasExternalLink && !isInternal ? (
            <ExternalLink className="ml-2 w-4 h-4" />
          ) : (
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform" />
          )}
        </span>
      </div>
    </>
  );

  return (
    <Link
      to={`/nyheter/${nyhet.slug}`}
      data-testid={`archive-news-card-${nyhet.id}`}
      className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-green-500/50 transition-all duration-300 animate-fade-in-up"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <CardContent />
    </Link>
  );
};

export const NewsArchive = () => {
  const { t } = useTranslation();
  const alleNyheter = useLocalizedNews().all();
  return (
    <div data-testid="news-archive-page" className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-green-500 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.backToHome')}
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-green-500/10 rounded-xl flex items-center justify-center">
              <Newspaper className="w-7 h-7 text-green-500" />
            </div>
            <div>
              <p className="text-green-500 font-medium uppercase tracking-widest mb-1">{t('news.archive')}</p>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white">
                {t('news.title')}
              </h1>
            </div>
          </div>
          
          <p className="text-slate-500 text-lg max-w-2xl">
            {t('news.intro')}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex flex-wrap gap-8 items-center justify-center text-center">
              <div>
                <p className="text-3xl font-bold text-green-500">{alleNyheter.length}</p>
                <p className="text-slate-500 text-sm">{t('news.total')}</p>
              </div>
              <div className="h-10 w-px bg-white/10 hidden sm:block"></div>
              <div>
                <p className="text-3xl font-bold text-green-500">
                  {alleNyheter.filter(n => ['Klubbnyheter','Klubbinfo'].includes(getNewsBySlug(n.slug)?.category)).length}
                </p>
                <p className="text-slate-500 text-sm">{t('news.club')}</p>
              </div>
              <div className="h-10 w-px bg-white/10 hidden sm:block"></div>
              <div>
                <p className="text-3xl font-bold text-green-500">
                  {alleNyheter.filter(n => getNewsBySlug(n.slug)?.category === 'Fotball').length}
                </p>
                <p className="text-slate-500 text-sm">{t('news.football')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {alleNyheter.map((nyhet, index) => (
              <NewsCard key={nyhet.id} nyhet={nyhet} index={index} />
            ))}
          </div>
          
          {/* Empty state message if no news */}
          {alleNyheter.length === 0 && (
            <div className="text-center py-20">
              <Newspaper className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">{t('news.none')}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsArchive;
